// Step 4: set up the tour, inside the project sidebar (sections nested under Set up tour). Live preview on the
// left (the real viewer in a frame), the chosen section on the right:
// start view, room names, hotspots, level names and tour options. Saving updates the database and the tour file.
import { DEFAULT_SETTINGS, WALK_SPEEDS, walkSpeedOf, levelName, computeLevels, assignLevels, readExr, type TourMode, type TourSettings } from '@archviewvr/engine';
import {
  getProject, getPanoramas, getLevels, getDraftManifest, draftUrl, uploadFile, update, insert, syncPublished,
  getHotspots, replaceHotspots, hotspotToManifest, suggestRooms, roomFromCameraName, getAlternates, deleteFile,
  type Project, type PanoRow, type LevelRow, type HotspotRow, unreadLeads } from '../data';
import { hotspotsPanel } from './hotspots-panel';
import { altsPanel, altFromRow, altProblems, schemeOrder, type AltEdit, type AltState } from './alts-panel';
import { html, raw, icon, $, $$, shell, stepPills, setShellProject, toast, confirmDialog, deviceSwitch, previewStage, wirePreview, SETUP_SECTIONS, type SetupSection } from '../ui';
import { openBilling, type Studio } from '../data';
import { getWork } from '../process';
import { depthPng, matchDepth } from '../depthfiles';
import { remove } from '../supa';

const OPTION_KEYS = ['fov', 'modes', 'startMode', 'roomLabels', 'sharpSize', 'markers', 'spin', 'walkSpeed', 'glide', 'settle', 'blendPower', 'blendHold',
  'joystickSide', 'invertLook', 'autoRotate', 'controlsOnTap', 'levelButtons', 'planMarkers', 'walkMarkers', 'compass', 'north',
  'snapshot', 'share', 'fullscreen', 'measure', 'help', 'cutSlider', 'phoneBalanced', 'expires', 'depthDetail', 'depthClean'] as const;
type Options = Pick<TourSettings, (typeof OPTION_KEYS)[number]>;
const SPEEDS = [{ v: WALK_SPEEDS.slow, label: 'Slow' }, { v: WALK_SPEEDS.normal, label: 'Normal' }, { v: WALK_SPEEDS.brisk, label: 'Brisk' }];
/** Salted PBKDF2 of a tour password; the server checks it the same way. */
async function hashPassword(password: string): Promise<{ access_hash: string; access_salt: string; access_token: string }> {
  const hex = (b: ArrayBuffer | Uint8Array) => [...new Uint8Array(b)].map((x) => x.toString(16).padStart(2, '0')).join('');
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const key = await crypto.subtle.importKey('raw', new TextEncoder().encode(password), 'PBKDF2', false, ['deriveBits']);
  const bits = await crypto.subtle.deriveBits({ name: 'PBKDF2', hash: 'SHA-256', salt, iterations: 100000 }, key, 256);
  return { access_hash: hex(bits), access_salt: hex(salt), access_token: hex(crypto.getRandomValues(new Uint8Array(12))) };
}
const MODE_LABEL: Record<TourMode, string> = { walk: 'Walk', hop: 'Hop', doll: '3D plan' };
const QUALITY: Array<{ v: number; label: string; note: string; warn?: string }> = [
  { v: 100000, label: 'Sharpest', note: 'Full-size render when they stop', warn: 'Can make phones crash and reload the tour. Best for tours viewed mostly on computers.' },
  { v: 4096, label: 'Balanced', note: 'Sharpens to 4K when they stop' },
  { v: 0, label: 'Fastest', note: 'No sharpening, least data' },
];
const ROOM_SUGGESTIONS = ['Entry', 'Hallway', 'Living', 'Kitchen', 'Dining', 'Family', 'Master bedroom', 'Ensuite', 'Bedroom 2', 'Bedroom 3',
  'Bedroom 4', 'Bathroom', 'Powder room', 'Laundry', 'Study', 'Media room', 'Rumpus', 'Stairs', 'Alfresco', 'Balcony', 'Terrace', 'Pool', 'Garage', 'Lobby', 'Rooftop'];

/** A level as edited here: hidden levels get no floor button; new ones come from regrouping. */
interface LevelEdit { id: string; name: string; height: number; hidden: boolean; isNew?: boolean }

interface State {
  start: { pano: string; heading: number; pitch: number } | null;
  rooms: Record<string, string>;
  /** Levels, lowest first. */
  levels: LevelEdit[];
  /** Which level each render is on (level id), or null between levels. */
  panoLevel: Record<string, string | null>;
  options: Options;
  hotspots: HotspotRow[];
  /** Alternate panoramas, as edited here, and as last saved (to tell what changed). */
  alt: AltState;
  altsSaved: AltEdit[];
  /** A new password to set, '' to remove it, undefined for no change. */
  password?: string;
  /** Depth map of each render (file and size), as edited here and as last saved; null = none. */
  depth: Record<string, { key: string; bytes: number } | null>;
  depthSaved: Record<string, { key: string; bytes: number } | null>;
  /** Buyers' tours use the depth maps (settings.depth). */
  depthOn: boolean;
  dirty: boolean;
}

export async function setupView(root: HTMLElement, studio: Studio, projectId: string, section: SetupSection = 'start'): Promise<void> {
  const page = shell(root, { studioName: studio.name, active: 'setup', sub: section, project: { id: projectId } });
  page.innerHTML = '<div class="loading-page">Loading tour setup</div>';
  const project = await getProject(projectId);
  if (!project) { page.innerHTML = html`<div class="empty"><h2>Project not found</h2><a class="btn" href="#/">Back to projects</a></div>`.html; return; }
  setShellProject(root, project, () => openBilling(project.id), unreadLeads(project.id));
  const [panos, levels, hotspots, altRows] = await Promise.all([getPanoramas(projectId), getLevels(projectId), getHotspots(projectId).catch(() => [] as HotspotRow[]),
    getAlternates(projectId).catch(() => [] as PanoRow[])]);
  if (!panos.length) { location.hash = `#/p/${projectId}`; return; }

  const saved = (project.settings ?? {}) as Partial<TourSettings>;
  const options = Object.fromEntries(OPTION_KEYS.map((k) => [k, saved[k] ?? DEFAULT_SETTINGS[k]])) as unknown as Options;
  options.walkSpeed = walkSpeedOf(options.walkSpeed);
  const st: State = {
    start: project.start_pano_id ? { pano: project.start_pano_id, heading: project.start_heading ?? 0, pitch: project.start_pitch ?? 0 } : null,
    rooms: Object.fromEntries(panos.map((p) => [p.id, p.room ?? ''])),
    levels: [...levels].sort((a, b) => +a.height - +b.height).map((l, i) => ({
      id: l.id, name: l.name || levelName(i), height: +l.height, hidden: (((saved as { hiddenLevels?: string[] }).hiddenLevels) ?? []).includes(l.id),
    })),
    panoLevel: Object.fromEntries(panos.map((p) => [p.id, p.level_id && levels.some((l) => l.id === p.level_id) ? p.level_id : null])),
    options,
    hotspots,
    alt: { alts: altRows.map((r) => altFromRow(r, panos)).filter((a): a is AltEdit => !!a), schemeDefault: saved.schemeDefault ?? '', compare: saved.compare !== false, order: saved.schemes ?? [] },
    altsSaved: [],
    depth: Object.fromEntries(panos.map((p) => [p.id, p.depth_key ? { key: p.depth_key, bytes: p.depth_bytes ?? 0 } : null])),
    depthSaved: {},
    depthOn: saved.depth !== false,
    dirty: false,
  };
  st.depthSaved = { ...st.depth };
  st.altsSaved = st.alt.alts.map((a) => ({ ...a }));
  render(root, page, project, panos, levels, st, section);
}

function render(root: HTMLElement, page: HTMLElement, project: Project, panos: PanoRow[], levels: LevelRow[], st: State, section: SetupSection): void {
  const thumb = (id: string) => draftUrl(project.id, `thumbs/${id}.jpg`);
  const levelLabel = (p: PanoRow) => st.levels.find((l) => l.id === st.panoLevel[p.id])?.name ?? null;

  page.innerHTML = html`<div class="setup">
    <div class="setup-top">
      <div class="setup-title"><h1>Set up tour</h1>${project.state === 'published' ? '' : stepPills(4, project.id, !!getWork(project.id)?.done)}</div>
      <div class="btn-row"><span class="muted small" id="save-note">All changes saved</span><button class="btn primary" id="save" disabled>Save</button></div>
    </div>
    <div class="setup-body">
      <div class="setup-stage">
        <div class="pv-bar-top"><span class="muted small">Preview</span><div class="pv-bar-r">
          <details class="check-menu"><summary class="btn small">Check tools</summary>
            <div class="check-pop">
              <p class="muted small">Only you see these, in the preview here. Buyers never do.</p>
              <label class="toggle"><input type="checkbox" id="dbg-edges"><span>Model edges <em>outline of your proxy model over the renders, to check they line up</em></span></label>
              <label class="toggle"><input type="checkbox" id="dbg-loads"><span>Render loading <em>colours each floor ring by how sharp its render has loaded</em></span></label>
              <div class="dbg-key" id="dbg-key" hidden><i style="background:#1f9d55"></i>Full size <i style="background:#e8890c"></i>Loading <i style="background:#d64545"></i>2K only</div>
              <label class="toggle"><input type="checkbox" id="dbg-removed"><span>Show what clean-up removes <em>in red, in the preview</em></span></label>
            </div>
          </details>${deviceSwitch()}</div></div>
        ${previewStage('preview', `/viewer/?draft=${project.id}${project.state === 'published' ? `&live=${project.slug}` : ''}`)}
        <div class="stage-hint">Drag to look around. This is the tour as buyers see it.</div>
      </div>
      <aside class="setup-panel">
        <h2 class="panel-title" id="panel-title"></h2>
        <section data-pane="start">
          <p class="muted">Where buyers begin. Look around in the preview, face the best view, then use it.</p>
          <div class="start-card" id="start-card"></div>
          <div class="btn-row">
            <button class="btn primary" id="use-view">Use what I’m looking at</button>
            <button class="btn" id="go-start">Go to start</button>
          </div>
          <label class="field"><span>Open the tour in</span><select id="start-mode"></select></label>
        </section>

        <div data-pane="levels"><h3 class="pane-h">Levels</h3><section id="lv-pane"></section></div>
        <section data-pane="rooms">
          <h3 class="pane-h">Rooms</h3>
          <p class="muted">Name the room each render is in. Buyers see the name at the top of the tour. Click a picture to look at it.</p>
          <div class="auto-rooms"><button class="btn" id="auto-rooms">${icon('spark', 16)}Fill in room names</button><span class="muted small">Looks at each render and suggests a name. Check them, then save.</span></div>
          <label class="toggle"><input type="checkbox" id="room-labels" ${st.options.roomLabels ? raw('checked') : ''}><span>Show room names in the tour</span></label>
          <datalist id="room-list">${ROOM_SUGGESTIONS.map((r) => html`<option value="${r}">`)}</datalist>
          <div class="room-list">
            ${panos.map((p) => { const ln = levelLabel(p); return html`<div class="room-row" data-pano="${p.id}">
              <button class="room-thumb" data-go="${p.id}" title="Look at ${p.name}"><img src="${thumb(p.id)}" alt="" loading="lazy"></button>
              <div class="room-fields"><input list="room-list" data-room="${p.id}" value="${st.rooms[p.id] ?? ''}" placeholder="Room name" aria-label="Room for ${p.name}">
                <em>${p.name}${ln ? ` · ${ln}` : ''}</em></div>
            </div>`; })}
          </div>
        </section>

        <section data-pane="hotspots" id="hs-pane"></section>

        <section data-pane="alts" id="alt-pane"></section>


        <section data-pane="options">
          <p class="muted small">Each group shows what it’s set to. Open one to change it.</p>
          <details class="opt-group" data-group="main" open>
            <summary><span class="og-t">Essentials</span><span class="og-s" id="sum-main"></span></summary>
            <div class="og-body">
          <div class="field"><span>Ways to move</span>
            <div class="checks">${(['walk', 'hop', 'doll'] as TourMode[]).map((m) => html`<label class="toggle"><input type="checkbox" data-mode="${m}" ${st.options.modes.includes(m) ? raw('checked') : ''}><span>${MODE_LABEL[m]} <em>${{ walk: 'move freely with keys or a joystick', hop: 'click to glide between renders', doll: 'fly out to a 3D model of the whole place' }[m]}</em></span></label>`)}</div>
          </div>
          <div class="field"><span>Picture quality</span>
            <div class="checks">${QUALITY.map((q) => html`<label class="toggle"><input type="radio" name="quality" value="${q.v}" ${nearestQuality(st.options.sharpSize) === q.v ? raw('checked') : ''}><span>${q.label} <em>${q.note}</em>${q.warn ? html`<em class="opt-warn">${q.warn}</em>` : ''}</span></label>
              ${q.warn ? html`<label class="toggle sub-toggle" id="phone-bal-row"><input type="checkbox" id="phone-bal" ${st.options.phoneBalanced !== false ? raw('checked') : ''}><span>Use Balanced on phones <em>Recommended. Computers still get the full-size render.</em></span></label>` : ''}`)}</div>
          </div>
          <div class="field"><span>Buttons in the tour</span>
          <div class="checks">
          <label class="toggle"><input type="checkbox" id="b-snap" ${st.options.snapshot ? raw('checked') : ''}><span>Snapshot <em>save a picture of the view</em></span></label>
          <label class="toggle"><input type="checkbox" id="b-share" ${st.options.share ? raw('checked') : ''}><span>Share</span></label>
          <label class="toggle"><input type="checkbox" id="b-full" ${st.options.fullscreen ? raw('checked') : ''}><span>Full screen <em>on computers and tablets</em></span></label>
          <label class="toggle"><input type="checkbox" id="b-measure" ${st.options.measure ? raw('checked') : ''}><span>Measure <em>tap two points for the distance</em></span></label>
          <label class="toggle"><input type="checkbox" id="b-help" ${st.options.help !== false ? raw('checked') : ''}><span>Help <em>shows how to move around, for a computer or a phone</em></span></label>

          </div></div>
            </div>
          </details>
          <details class="opt-group" data-group="moving">
            <summary><span class="og-t">Moving around</span><span class="og-s" id="sum-moving"></span></summary>
            <div class="og-body">
          <div class="field"><span>Walking speed</span>
            <div class="seg" id="speed">${SPEEDS.map((x) => html`<button data-speed="${x.v}">${x.label}</button>`)}</div></div>
          <label class="field"><span>Glide time <b id="glide-v"></b></span><input type="range" id="glide" min="0.5" max="2" step="0.1" value="${st.options.glide}"></label>
          <label class="toggle"><input type="checkbox" id="settle" ${st.options.settle ? raw('checked') : ''}><span>Snap to the nearest view when they stop <em>keeps it sharpest; off feels freer</em></span></label>
          <label class="field"><span>Blending between renders <b id="blend-v"></b></span><input type="range" id="blend" min="1" max="5" step="0.5" value="${st.options.blendPower ?? 2}">
            <em class="hint">Soft mixes nearby renders more, for smoother walking. Crisp lets the nearest render take over sooner, for sharper detail.</em></label>
          <label class="field"><span>Full render near each view <b id="hold-v"></b></span><input type="range" id="hold" min="0" max="1.5" step="0.1" value="${st.options.blendHold ?? 0}">
            <em class="hint">Within this distance of a render, buyers see only that render, with no blending.</em></label>
          <div class="field"><span>Joystick on phones</span>
            <div class="seg" id="joy-side"><button data-side="left">Left</button><button data-side="right">Right</button></div></div>
          <label class="toggle"><input type="checkbox" id="invert" ${st.options.invertLook ? raw('checked') : ''}><span>Drag the other way to look around</span></label>
            </div>
          </details>
          <details class="opt-group" data-group="look">
            <summary><span class="og-t">What buyers see</span><span class="og-s" id="sum-look"></span></summary>
            <div class="og-body">
          <label class="field"><span>Field of view <b id="fov-v">${st.options.fov}°</b></span><input type="range" id="fov" min="70" max="110" step="5" value="${st.options.fov}"></label>
          <label class="toggle"><input type="checkbox" id="auto-rotate" ${st.options.autoRotate ? raw('checked') : ''}><span>Look around slowly when it opens <em>until someone touches it; good on a website</em></span></label>
          <label class="toggle"><input type="checkbox" id="on-tap" ${st.options.controlsOnTap ? raw('checked') : ''}><span>Hide the controls until a tap or click</span></label>
          <label class="toggle"><input type="checkbox" id="markers" ${st.options.markers ? raw('checked') : ''}><span>Floor markers <em>rings where each render was taken</em></span></label>
          <label class="toggle"><input type="checkbox" id="walk-rings" ${st.options.walkMarkers !== false ? raw('checked') : ''}><span>Circles on the floor in walk mode <em>where each render was taken; hop mode always shows them</em></span></label>
            </div>
          </details>
          <details class="opt-group" data-group="plan">
            <summary><span class="og-t">3D plan</span><span class="og-s" id="sum-plan"></span></summary>
            <div class="og-body">
          <label class="toggle"><input type="checkbox" id="spin" ${st.options.spin ? raw('checked') : ''}><span>Slow spin in the 3D plan <em>when nobody is touching it</em></span></label>

          <label class="toggle"><input type="checkbox" id="level-btns" ${st.options.levelButtons ? raw('checked') : ''}><span>Floor buttons in the 3D plan</span></label>
          <label class="toggle"><input type="checkbox" id="plan-rings" ${st.options.planMarkers ? raw('checked') : ''}><span>Circles in the 3D plan <em>where each render was taken</em></span></label>
          <label class="toggle"><input type="checkbox" id="b-cut" ${st.options.cutSlider !== false ? raw('checked') : ''}><span>Slice slider in the 3D plan <em>lowers the cut through the model, down to the floor</em></span></label>
          <label class="toggle"><input type="checkbox" id="compass" ${st.options.compass ? raw('checked') : ''}><span>North arrow in the 3D plan</span></label>
          <label class="field" id="north-row"><span>Which way is north <b id="north-v"></b></span><input type="range" id="north" min="-180" max="180" step="1" value="${st.options.north}">
            <em class="muted small hint">Open the 3D plan in the preview and turn until the arrow points north.</em></label>
            </div>
          </details>
          <details class="opt-group" data-group="depth" data-anchor="depth">
            <summary><span class="og-t">Depth maps</span><span class="og-s" id="sum-depth"></span></summary>
            <div class="og-body">
          <p class="muted">With a depth map, furniture keeps its shape as buyers walk between renders. Render a CZDepth pass (EXR) with each panorama and drop them here. <a href="https://archviewvr.com/help/setting-up/depth-maps" target="_blank" rel="noopener">How to render them</a></p>
          <label class="depth-drop" id="depth-drop"><input type="file" id="depth-files" accept=".exr" multiple><span>Choose EXR files or drop them here</span></label>
          <p class="muted small" id="depth-count"></p>
          <ul class="depth-list" id="depth-list" hidden></ul>
          <label class="toggle"><input type="checkbox" id="depth-use" ${st.depthOn ? raw('checked') : ''}><span>Use depth maps <em>turn off to show the tour on the proxy only</em></span></label>
          <div class="field"><span>Mesh detail <em class="muted small">higher follows thin things like chair legs; phones stay at standard</em></span>
            <div class="seg" id="depth-detail">${(['low', 'standard', 'high'] as const).map((x) => html`<button data-detail="${x}">${x[0].toUpperCase() + x.slice(1)}</button>`)}</div></div>
          <label class="field"><span>Clean-up <b id="depth-clean-v"></b></span><input type="range" id="depth-clean" min="0" max="1" step="0.25" value="${st.options.depthClean ?? 0.5}">
            <em class="muted small hint">Removes stray shapes in open space: anything a nearby render sees straight through, and small loose pieces. Turn it up if odd shapes float in walkways, down if thin things go missing.</em></label>
          <div class="depth-stat"><button class="btn small" id="depth-rebuild" type="button">Rebuild</button><span class="muted small" id="depth-stat">Walk the preview to build the depth meshes.</span></div>
            </div>
          </details>
          <details class="opt-group" data-group="access">
            <summary><span class="og-t">Access</span><span class="og-s" id="sum-access"></span></summary>
            <div class="og-body">
          <div class="field"><span>Password</span>
            <div class="pw-row"><input type="password" id="pw" placeholder="${project.access_hash ? 'Password set · type a new one to change it' : 'No password'}" autocomplete="new-password">
              ${project.access_hash ? html`<button class="link-btn" id="pw-clear">Remove</button>` : ''}</div>
            <em class="muted small hint" id="pw-note">${project.access_hash ? 'Buyers need the password to open this tour.' : 'Anyone with the link can open the tour.'}</em></div>
          <label class="field"><span>Last day to view <em class="muted">optional</em></span>
            <div class="pw-row"><input type="date" id="expires" value="${st.options.expires ?? ''}"><button class="link-btn" id="expires-clear">Clear</button></div>
            <em class="muted small hint">After this day the tour shows “no longer available”. Your files are kept.</em></label>
            </div>
          </details>
        </section>
      </aside>
    </div>
  </div>`.html;
  const root0 = root; root = page; // everything below lives in the page

  const frame = $<HTMLIFrameElement>('#preview', root);
  wirePreview($('.setup-stage', root));
  // check tools (Options): only this preview sees them
  const sendDebug = () => frame.contentWindow?.postMessage({ avr: 'debug', edges: $<HTMLInputElement>('#dbg-edges', root).checked, loads: $<HTMLInputElement>('#dbg-loads', root).checked, removed: $<HTMLInputElement>('#dbg-removed', root).checked }, location.origin);
  // the Check tools menu shuts when you click elsewhere
  const checkMenu = root.querySelector<HTMLDetailsElement>('.check-menu');
  const shutCheck = (e: MouseEvent) => { if (checkMenu?.open && !checkMenu.contains(e.target as Node)) checkMenu.open = false; };
  document.addEventListener('click', shutCheck);
  addEventListener('hashchange', () => document.removeEventListener('click', shutCheck), { once: true });
  for (const id of ['#dbg-edges', '#dbg-loads', '#dbg-removed']) $<HTMLInputElement>(id, root).onchange = () => { $('#dbg-key', root).hidden = !$<HTMLInputElement>('#dbg-loads', root).checked; sendDebug(); };
  const send = (msg: object) => frame.contentWindow?.postMessage(msg, location.origin);
  wireDepth(root, project, panos, frame, st, () => touch());
  wireDepthStats(root, frame);
  const pushSettings = () => send({ avr: 'settings', settings: { ...st.options, ...altSettings(st) } });
  const shownLevels = () => st.levels.map((l) => ({ name: l.name, height: l.height, hidden: l.hidden }));
  const pushNames = () => send({ avr: 'names', rooms: st.rooms, levels: shownLevels() });
  let ready = false;

  const touch = () => {
    st.dirty = true;
    const b = $<HTMLButtonElement>('#save', root);
    b.disabled = false; b.textContent = 'Save';
    $('#save-note', root).textContent = 'Unsaved changes';
  };

  // sections: chosen in the sidebar (nested under Set up tour); switching keeps the preview and unsaved changes
  const showSection = (sec: SetupSection) => {
    // Levels sit with Rooms now (old links to levels open there)
    if (sec === 'levels') sec = 'rooms';
    const shown = sec === 'rooms' ? ['levels', 'rooms'] : [sec];
    $$<HTMLElement>('[data-pane]', page).forEach((p) => (p.hidden = !shown.includes(p.dataset.pane!)));
    $('#panel-title', page).textContent = SETUP_SECTIONS.find((x) => x.id === sec)!.label;
    $$<HTMLAnchorElement>('.nav-sub [data-sec]', root0).forEach((a) => a.classList.toggle('on', a.dataset.sec === sec));
    history.replaceState(null, '', `#/p/${project.id}/setup/${sec}`);
  };
  $$<HTMLAnchorElement>('.nav-sub [data-sec]', root0).forEach((a) => (a.onclick = (e) => { e.preventDefault(); showSection(a.dataset.sec as SetupSection); }));
  showSection(section);

  // start view
  const drawStart = () => {
    const p = st.start && panos.find((x) => x.id === st.start!.pano);
    $('#start-card', root).innerHTML = p
      ? html`<img src="${thumb(p.id)}" alt=""><div><strong>${st.rooms[p.id] || p.name}</strong><span class="muted">Facing ${Math.round(((st.start!.heading % 360) + 360) % 360)}°</span></div>`.html
      : html`<div class="muted">Not set. The tour opens at the first render.</div>`.html;
    const sel = $<HTMLSelectElement>('#start-mode', root);
    sel.innerHTML = st.options.modes.map((m) => html`<option value="${m}" ${m === st.options.startMode ? raw('selected') : ''}>${MODE_LABEL[m]}</option>`.html).join('');
  };
  drawStart();
  $('#use-view', root).onclick = () => {
    if (!ready) { toast('The preview is still loading'); return; }
    const once = (e: MessageEvent) => {
      if (e.origin !== location.origin || e.data?.avr !== 'view') return;
      removeEventListener('message', once);
      if (!e.data.pano) return;
      if (e.data.mode === 'doll') { toast('Go into a room first, then use the view'); return; }
      st.start = { pano: e.data.pano, heading: e.data.heading, pitch: e.data.pitch };
      drawStart(); touch(); toast('Start view set');
    };
    addEventListener('message', once);
    send({ avr: 'get-view' });
  };
  $('#go-start', root).onclick = () => {
    const s = st.start ?? (panos[0] ? { pano: panos[0].id, heading: undefined, pitch: undefined } : null);
    if (s) send({ avr: 'go', ...s });
  };
  $<HTMLSelectElement>('#start-mode', root).onchange = (e) => { st.options.startMode = (e.target as HTMLSelectElement).value as TourMode; touch(); };

  // rooms
  $$<HTMLButtonElement>('[data-go]', root).forEach((b) => (b.onclick = () => send({ avr: 'go', pano: b.dataset.go })));
  $$<HTMLInputElement>('[data-room]', root).forEach((inp) => {
    inp.oninput = () => { st.rooms[inp.dataset.room!] = inp.value.trim(); touch(); pushNames(); if (st.start?.pano === inp.dataset.room) drawStart(); };
    inp.onfocus = () => send({ avr: 'go', pano: inp.dataset.room });
  });
  $('#auto-rooms', root).onclick = async () => {
    const btn = $<HTMLButtonElement>('#auto-rooms', root);
    const filled = panos.filter((p) => st.rooms[p.id]).length;
    let replace = false;
    if (filled) {
      replace = confirm(`${filled} render${filled === 1 ? ' already has a name' : 's already have names'}. Replace those too?\n\nOK replaces every name. Cancel only fills the empty ones.`);
    }
    btn.disabled = true; btn.innerHTML = html`${icon('spin', 16)}Looking at your renders…`.html;
    let rooms: Record<string, string> = {};
    let how = 'by looking at each render';
    try { rooms = (await suggestRooms(project.id)).rooms; }
    catch (e) {
      // no AI key yet (or it failed): use words in the camera names instead
      for (const p of panos) { const r = roomFromCameraName(p.name); if (r) rooms[p.id] = r; }
      how = 'from your camera names';
      if (!(e instanceof Error && /switched on/.test(e.message))) console.warn(e);
    }
    let n = 0;
    for (const p of panos) {
      const r = rooms[p.id];
      if (!r || (st.rooms[p.id] && !replace)) continue;
      st.rooms[p.id] = r; n++;
      const inp = $<HTMLInputElement>(`[data-room="${p.id}"]`, root);
      if (inp) { inp.value = r; inp.classList.add('suggested'); inp.addEventListener('input', () => inp.classList.remove('suggested'), { once: true }); }
    }
    btn.disabled = false; btn.innerHTML = html`${icon('spark', 16)}Fill in room names`.html;
    if (n) { touch(); pushNames(); drawStart(); toast(`${n} room${n === 1 ? '' : 's'} named ${how}. Check the highlighted ones, then save.`, 'info', 7000); }
    else toast(how.startsWith('from') ? 'No room words found in your camera names. Name them here, or ask us to switch on automatic names.' : 'No new names to add.', 'info', 7000);
  };
  $<HTMLInputElement>('#room-labels', root).onchange = (e) => { st.options.roomLabels = (e.target as HTMLInputElement).checked; touch(); pushSettings(); };

  // levels: names, view one in the preview, hide its floor button, delete, regroup from the cameras
  const levelCount = (id: string) => panos.filter((p) => st.panoLevel[p.id] === id).length;
  const between = () => panos.filter((p) => !st.panoLevel[p.id]).length;
  const reassign = (only?: Set<string>) => {
    // renders go to their closest level within 1 m
    const lv = st.levels;
    const at = assignLevels(lv.map((l) => l.height), panos.map((p) => +p.y));
    panos.forEach((p, i) => { if (!only || only.has(p.id)) st.panoLevel[p.id] = at[i] == null ? null : lv[at[i]!].id; });
  };
  const levelsChanged = () => { touch(); pushNames(); drawLevels(); };
  const drawLevels = () => {
    const pane = $('#lv-pane', root);
    const empty = st.levels.filter((l) => !levelCount(l.id));
    const b = between();
    pane.innerHTML = html`
      <p class="muted">Floor buttons in the 3D plan, lowest first. <b>View</b> shows a level in the preview. Hide a level to drop its floor button; its renders can still be walked to.</p>
      <div class="lv-tools">
        <button class="btn small" id="lv-regroup" title="Works out the levels again from the height of each camera">${icon('spark', 15)}Regroup from cameras</button>
        ${empty.length ? html`<button class="btn small" id="lv-empty">${icon('trash', 15)}Remove ${empty.length} empty level${empty.length === 1 ? '' : 's'}</button>` : ''}
      </div>
      <div class="lv-list">
        ${st.levels.map((l, i) => { const n = levelCount(l.id); return html`<div class="lv-row ${l.hidden ? 'off' : ''} ${n ? '' : 'lv-none'}" data-lv="${l.id}">
          <input data-lv-name="${l.id}" value="${l.name}" placeholder="${levelName(i)}" aria-label="Name for the level at ${l.height.toFixed(1)} m">
          <div class="lv-foot"><span class="lv-meta">${l.height.toFixed(1)} m · ${n ? `${n} render${n === 1 ? '' : 's'}` : 'no renders'}${l.hidden ? ' · hidden' : ''}</span>
          <div class="lv-btns">
            <button class="btn small" data-lv-view="${l.id}" ${l.hidden ? raw('disabled title="Unhide it to view it"') : raw('title="Show this level in the preview"')}>${icon('eye', 14)}View</button>
            <button class="btn small" data-lv-hide="${l.id}" aria-pressed="${l.hidden ? 'true' : 'false'}">${l.hidden ? 'Unhide' : 'Hide'}</button>
            <button class="btn small icon-only" data-lv-del="${l.id}" title="Delete this level" aria-label="Delete ${l.name}">${icon('trash', 14)}</button>
          </div></div>
        </div>`; })}
      </div>
      ${b ? html`<p class="muted small">${b} render${b === 1 ? ' is' : 's are'} between levels (stairs or landings). They’re walkable but have no floor button.</p>` : ''}`.html;
    $$<HTMLInputElement>('[data-lv-name]', pane).forEach((inp) => (inp.oninput = () => {
      const l = st.levels.find((x) => x.id === inp.dataset.lvName)!;
      l.name = inp.value.trim() || levelName(st.levels.indexOf(l)); touch(); pushNames();
    }));
    $$<HTMLButtonElement>('[data-lv-view]', pane).forEach((btn) => (btn.onclick = () => {
      const l = st.levels.find((x) => x.id === btn.dataset.lvView)!;
      send({ avr: 'show-level', height: l.height });
    }));
    $$<HTMLButtonElement>('[data-lv-hide]', pane).forEach((btn) => (btn.onclick = () => {
      const l = st.levels.find((x) => x.id === btn.dataset.lvHide)!;
      l.hidden = !l.hidden; levelsChanged();
    }));
    $$<HTMLButtonElement>('[data-lv-del]', pane).forEach((btn) => (btn.onclick = async () => {
      const l = st.levels.find((x) => x.id === btn.dataset.lvDel)!;
      const n = levelCount(l.id);
      if (n && !(await confirmDialog({ title: `Delete ${l.name}?`, body: `Its ${n} render${n === 1 ? '' : 's'} move to the closest level, or sit between levels if none is near. Nothing changes until you save.`, confirm: 'Delete level', danger: true }))) return;
      const moved = new Set(panos.filter((p) => st.panoLevel[p.id] === l.id).map((p) => p.id));
      st.levels = st.levels.filter((x) => x !== l);
      reassign(moved);
      levelsChanged();
    }));
    $('#lv-empty', pane)?.addEventListener('click', () => {
      const n = empty.length;
      st.levels = st.levels.filter((l) => levelCount(l.id));
      levelsChanged();
      toast(`${n} empty level${n === 1 ? '' : 's'} removed. Save to keep it.`);
    });
    $('#lv-regroup', pane).onclick = () => {
      const old = st.levels;
      const heights = computeLevels(panos.map((p) => +p.y));
      const used = new Set<string>();
      st.levels = heights.map((h, i) => {
        // keep the name (and hidden choice) of a level that was already at about this height
        const match = old.filter((l) => !used.has(l.id) && Math.abs(l.height - h) < 0.5).sort((a, c) => (Math.abs(a.height - h) - Math.abs(c.height - h)) || (levelCount(c.id) - levelCount(a.id)))[0];
        if (match) used.add(match.id);
        return { id: crypto.randomUUID(), isNew: true, name: match?.name ?? levelName(i), height: h, hidden: match?.hidden ?? false };
      });
      reassign();
      levelsChanged();
      toast(`${st.levels.length} level${st.levels.length === 1 ? '' : 's'} from your cameras. Check the names, then save.`, 'info', 6000);
    };
  };
  drawLevels();

  // options
  $$<HTMLInputElement>('input[data-mode]', root).forEach((c) => (c.onchange = () => {
    const modes = $$<HTMLInputElement>('input[data-mode]', root).filter((x) => x.checked).map((x) => x.dataset.mode as TourMode);
    if (!modes.length) { c.checked = true; toast('Keep at least one way to move'); return; }
    st.options.modes = modes;
    if (!modes.includes(st.options.startMode)) st.options.startMode = modes[0];
    drawStart(); touch(); pushSettings();
  }));
  const fov = $<HTMLInputElement>('#fov', root);
  fov.oninput = () => { st.options.fov = +fov.value; $('#fov-v', root).textContent = `${fov.value}°`; touch(); pushSettings(); };
  const drawPhoneBal = () => { $('#phone-bal-row', root).hidden = nearestQuality(st.options.sharpSize) !== QUALITY[0].v; };
  $$<HTMLInputElement>('input[name=quality]', root).forEach((r) => (r.onchange = () => { st.options.sharpSize = +r.value; touch(); pushSettings(); drawPhoneBal(); }));
  $<HTMLInputElement>('#phone-bal', root).onchange = (e) => { st.options.phoneBalanced = (e.target as HTMLInputElement).checked; touch(); pushSettings(); };
  drawPhoneBal();
  $<HTMLInputElement>('#markers', root).onchange = (e) => { st.options.markers = (e.target as HTMLInputElement).checked; touch(); pushSettings(); };
  $<HTMLInputElement>('#spin', root).onchange = (e) => { st.options.spin = (e.target as HTMLInputElement).checked; touch(); pushSettings(); };

  // more options
  const set = <K extends keyof Options>(k: K, v: Options[K]) => { st.options[k] = v; touch(); pushSettings(); drawOptions(); };
  const toggle = (id: string, k: 'settle' | 'invertLook' | 'autoRotate' | 'controlsOnTap' | 'levelButtons' | 'planMarkers' | 'walkMarkers' | 'compass' | 'snapshot' | 'share' | 'fullscreen' | 'measure' | 'help' | 'cutSlider') =>
    ($<HTMLInputElement>(id, root).onchange = (e) => set(k, (e.target as HTMLInputElement).checked));
  toggle('#settle', 'settle'); toggle('#invert', 'invertLook'); toggle('#auto-rotate', 'autoRotate'); toggle('#on-tap', 'controlsOnTap');
  toggle('#level-btns', 'levelButtons'); toggle('#plan-rings', 'planMarkers'); toggle('#walk-rings', 'walkMarkers'); toggle('#compass', 'compass');
  toggle('#b-snap', 'snapshot'); toggle('#b-share', 'share'); toggle('#b-full', 'fullscreen'); toggle('#b-measure', 'measure'); toggle('#b-help', 'help'); toggle('#b-cut', 'cutSlider');
  $$<HTMLButtonElement>('[data-speed]', root).forEach((b) => (b.onclick = () => set('walkSpeed', +b.dataset.speed!)));
  $$<HTMLButtonElement>('[data-side]', root).forEach((b) => (b.onclick = () => set('joystickSide', b.dataset.side as 'left' | 'right')));
  $<HTMLInputElement>('#glide', root).oninput = (e) => set('glide', +(e.target as HTMLInputElement).value);
  $<HTMLInputElement>('#blend', root).oninput = (e) => set('blendPower', +(e.target as HTMLInputElement).value);
  $<HTMLInputElement>('#hold', root).oninput = (e) => set('blendHold', +(e.target as HTMLInputElement).value);
  $$<HTMLButtonElement>('[data-detail]', root).forEach((b) => (b.onclick = () => set('depthDetail', b.dataset.detail as 'low' | 'standard' | 'high')));
  $<HTMLInputElement>('#depth-clean', root).oninput = (e) => set('depthClean', +(e.target as HTMLInputElement).value);
  $<HTMLInputElement>('#north', root).oninput = (e) => set('north', +(e.target as HTMLInputElement).value);
  $<HTMLInputElement>('#expires', root).onchange = (e) => set('expires', (e.target as HTMLInputElement).value || undefined);
  $('#expires-clear', root).onclick = () => { $<HTMLInputElement>('#expires', root).value = ''; set('expires', undefined); };
  $<HTMLInputElement>('#pw', root).oninput = (e) => {
    const v = (e.target as HTMLInputElement).value;
    st.password = v ? v : undefined;
    $('#pw-note', root).textContent = v ? 'Saved with the tour. Buyers will need it to open the tour.' : project.access_hash ? 'Buyers need the password to open this tour.' : 'Anyone with the link can open the tour.';
    touch();
  };
  $('#pw-clear', root)?.addEventListener('click', () => {
    st.password = ''; $<HTMLInputElement>('#pw', root).value = '';
    $('#pw-note', root).textContent = 'The password comes off when you save. Anyone with the link can open the tour.';
    touch();
  });
  const drawOptions = () => {
    $$<HTMLButtonElement>('[data-speed]', root).forEach((b) => b.setAttribute('aria-pressed', String(Math.abs(+b.dataset.speed! - st.options.walkSpeed) < 0.1)));
    $$<HTMLButtonElement>('[data-side]', root).forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.side === (st.options.joystickSide ?? 'left'))));
    $('#glide-v', root).textContent = `${st.options.glide.toFixed(1)}×`;
    const bp = st.options.blendPower ?? 2;
    $('#blend-v', root).textContent = bp <= 1.5 ? 'Soft' : bp <= 2.5 ? 'Normal' : bp <= 3.5 ? 'Crisp' : 'Very crisp';
    const bh = st.options.blendHold ?? 0;
    $('#hold-v', root).textContent = bh > 0 ? `${bh.toFixed(1)} m` : 'Off';
    $('#north-v', root).textContent = `${Math.round(st.options.north ?? 0)}°`;
    $$<HTMLButtonElement>('[data-detail]', root).forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.detail === (st.options.depthDetail ?? 'standard'))));
    const dc = st.options.depthClean ?? 0.5;
    $('#depth-clean-v', root).textContent = dc <= 0 ? 'Off' : dc <= 0.25 ? 'Light' : dc <= 0.5 ? 'Normal' : dc <= 0.75 ? 'Strong' : 'Strongest';
    $('#north-row', root).hidden = !st.options.compass;
    drawSummaries();
  };
  // each group's heading says what it's set to, so the list reads at a glance with every group shut
  const drawSummaries = () => {
    const o = st.options;
    const on = (list: Array<[boolean | undefined, string]>, none = 'Nothing extra') => list.filter(([b]) => b).map(([, t]) => t).join(' · ') || none;
    const sum = (k: string, t: string) => { const el = root.querySelector<HTMLElement>(`#sum-${k}`); if (el) el.textContent = t; };
    const q = QUALITY.find((x) => x.v === nearestQuality(o.sharpSize));
    const buttons = [o.snapshot, o.share, o.fullscreen, o.measure, o.help !== false].filter(Boolean).length;
    sum('main', `${o.modes.map((m) => MODE_LABEL[m]).join(', ')} · ${q?.label ?? ''} pictures · ${buttons} button${buttons === 1 ? '' : 's'}`);
    const speed = SPEEDS.reduce((b, x) => (Math.abs(x.v - o.walkSpeed) < Math.abs(b.v - o.walkSpeed) ? x : b), SPEEDS[0]).label;
    const bp = o.blendPower ?? 2;
    sum('moving', `${speed} speed · glide ${o.glide.toFixed(1)}× · ${o.settle ? 'snaps to views' : 'no snapping'} · ${bp <= 1.5 ? 'soft' : bp <= 2.5 ? 'normal' : 'crisp'} blending`);
    sum('look', on([[true, `${o.fov}° view`], [o.autoRotate, 'looks around on open'], [o.controlsOnTap, 'controls on tap'], [o.markers, 'floor markers'], [o.walkMarkers !== false, 'circles in walk']]));
    sum('plan', on([[o.spin, 'slow spin'], [o.levelButtons, 'floor buttons'], [o.planMarkers, 'circles'], [o.cutSlider !== false, 'slice slider'], [o.compass, 'north arrow']]));
    const count = root.querySelector('#depth-count')?.textContent?.trim();
    const dc = o.depthClean ?? 0.5;
    sum('depth', st.depthOn === false ? 'Turned off' : `${count || 'None yet'}${count && !/^no /i.test(count) ? ` · ${o.depthDetail ?? 'standard'} detail · clean-up ${dc <= 0 ? 'off' : dc <= 0.25 ? 'light' : dc <= 0.5 ? 'normal' : dc <= 0.75 ? 'strong' : 'strongest'}` : ''}`);
    const pw = st.password !== undefined ? (st.password ? 'New password' : 'Password comes off') : project.access_hash ? 'Password set' : 'Anyone with the link';
    sum('access', `${pw} · ${o.expires ? `until ${new Date(o.expires + 'T00:00').toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}` : 'no end date'}`);
  };
  const optPane = root.querySelector<HTMLElement>('[data-pane="options"]');
  optPane?.addEventListener('change', () => requestAnimationFrame(drawSummaries));
  optPane?.addEventListener('input', () => requestAnimationFrame(drawSummaries));
  const depthCount = root.querySelector('#depth-count');
  if (depthCount) new MutationObserver(drawSummaries).observe(depthCount, { childList: true, characterData: true, subtree: true });
  drawOptions();

  // hotspots
  const hs = hotspotsPanel($('#hs-pane', root), project, st.hotspots, send, touch);
  // alternate panoramas
  const alt = altsPanel($('#alt-pane', root), project, panos, st.alt, (p) => st.rooms[p.id] || p.name, send, touch, pushSettings);

  // the preview says when it has loaded; then show the current (unsaved) state in it
  const onMsg = (e: MessageEvent) => {
    if (e.origin !== location.origin || e.source !== frame.contentWindow) return;
    const d = e.data ?? {};
    if (d.avr === 'ready') { ready = true; pushSettings(); pushNames(); hs.push(); alt.push(); sendDebug(); }
    else if (d.avr === 'placed' && Array.isArray(d.position)) hs.placed(d.position, Array.isArray(d.normal) ? d.normal : undefined);
    else if (d.avr === 'hotspot-opened') {
      hs.opened(d.id);
      showSection('hotspots'); // jump to Hotspots when one is clicked in the preview
    }
  };
  addEventListener('message', onMsg);
  addEventListener('hashchange', () => removeEventListener('message', onMsg), { once: true });

  // leaving with unsaved changes
  const guard = (e: BeforeUnloadEvent) => { if (st.dirty) { e.preventDefault(); e.returnValue = ''; } };
  addEventListener('beforeunload', guard);
  addEventListener('hashchange', () => removeEventListener('beforeunload', guard), { once: true });
  // any other link while there are unsaved changes: ask first
  root0.addEventListener('click', (e) => {
    const a = (e.target as HTMLElement).closest<HTMLAnchorElement>('a[href^="#/"]');
    if (!a || a.dataset.sec || !st.dirty) return;
    if (!confirm('Leave without saving your changes?')) { e.preventDefault(); e.stopPropagation(); }
  }, true);

  $<HTMLButtonElement>('#save', root).onclick = async () => {
    const b = $<HTMLButtonElement>('#save', root);
    b.disabled = true; b.textContent = 'Saving…';
    try {
      await saveSetup(project, panos, levels, st);
      st.dirty = false;
      b.textContent = 'Saved';
      $('#save-note', root).textContent = project.state === 'published' ? 'Saved. The live tour is updated.' : 'All changes saved';
      toast('Tour set up saved');
    } catch (e) {
      b.disabled = false; b.textContent = 'Save';
      toast(`Couldn’t save: ${e instanceof Error ? e.message : 'unknown error'}`, 'error', 8000);
    }
  };
}

/** The tour settings for alternate panoramas: the main renders' button name, the slider, and the scheme order. */
function altSettings(st: State): { schemeDefault: string; compare: boolean; schemes: string[] } {
  return { schemeDefault: st.alt.schemeDefault.trim(), compare: st.alt.compare, schemes: schemeOrder(st.alt.alts, st.alt.order) };
}

function nearestQuality(v: number): number {
  return QUALITY.reduce((a, q) => (Math.abs(q.v - v) < Math.abs(a - v) ? q.v : a), QUALITY[1].v);
}

/**
 * Set up tour → Options → Depth maps: EXR depth passes are read here, checked against the proxy in the
 * preview (which works out the CZDepth range), written as PNG and uploaded straight away; Save records them.
 */
function wireDepth(root: HTMLElement, project: Project, panos: PanoRow[], frame: HTMLIFrameElement, st: State, touch: () => void): void {
  const input = $<HTMLInputElement>('#depth-files', root), drop = $('#depth-drop', root), list = $('#depth-list', root);
  const notes = new Map<string, { text: string; state: 'ok' | 'warn' | 'busy' }>();
  const send = (msg: object, t?: Transferable[]) => frame.contentWindow?.postMessage(msg, location.origin, t ?? []);
  const draw = () => {
    const withMap = panos.filter((p) => st.depth[p.id]);
    $('#depth-count', root).textContent = withMap.length ? `${withMap.length} of ${panos.length} renders have a depth map` : 'No depth maps yet';
    const shown = panos.filter((p) => st.depth[p.id] || notes.has(p.id));
    const strays = [...notes.keys()].filter((k) => !panos.some((p) => p.id === k));
    list.hidden = !shown.length && !strays.length;
    list.innerHTML = [
      ...shown.map((p) => {
        const n = notes.get(p.id);
        const text = n?.text ?? (st.depth[p.id]?.key === st.depthSaved[p.id]?.key ? 'Saved' : 'Ready · save to keep');
        return html`<li class="${n?.state ?? 'ok'}"><b>${st.rooms[p.id] || p.name}</b><span>${text}</span>${st.depth[p.id] ? html`<button class="link-btn" data-depth-remove="${p.id}">Remove</button>` : ''}</li>`.html;
      }),
      ...strays.map((k) => html`<li class="warn"><b>${k}</b><span>${notes.get(k)!.text}</span></li>`.html),
    ].join('');
  };
  draw();
  list.onclick = (e) => {
    const id = (e.target as HTMLElement).closest<HTMLElement>('[data-depth-remove]')?.dataset.depthRemove;
    if (!id) return;
    const cur = st.depth[id];
    // a map added since the last save isn't needed by anything: its file goes now
    if (cur && cur.key !== st.depthSaved[id]?.key) void deleteFile(project.id, cur.key);
    st.depth[id] = null; notes.delete(id);
    send({ avr: 'depth-drop', id });
    touch(); draw();
  };
  const take = async (files: File[]) => {
    const read: Array<{ file: File; raw: Float32Array; w: number; h: number; cam: number[] | null } | null> = [];
    for (const f of files) {
      try { const im = await readExr(await f.arrayBuffer()); read.push({ file: f, raw: im.data, w: im.width, h: im.height, cam: im.cameraTransform }); }
      catch (e) { notes.set(f.name, { text: e instanceof Error ? e.message : 'Couldn’t read this file', state: 'warn' }); read.push(null); }
    }
    const ok = read.filter((r): r is NonNullable<typeof r> => !!r);
    const match = matchDepth(ok.map((r) => ({ name: r.file.name, cam: r.cam })), panos);
    const items: Array<{ id: string; raw: Float32Array; w: number; h: number }> = [];
    ok.forEach((r, i) => {
      const p = match[i];
      if (!p) { notes.set(r.file.name, { text: 'No render with this name or camera', state: 'warn' }); return; }
      notes.set(p.id, { text: 'Checking against the model', state: 'busy' });
      items.push({ id: p.id, raw: r.raw, w: r.w, h: r.h });
    });
    draw();
    if (items.length) send({ avr: 'depth-try', items }, items.map((i) => i.raw.buffer));
  };
  input.onchange = () => { if (input.files?.length) void take([...input.files]); input.value = ''; };
  drop.ondragover = (e) => { e.preventDefault(); drop.classList.add('over'); };
  drop.ondragleave = () => drop.classList.remove('over');
  drop.ondrop = (e) => { e.preventDefault(); drop.classList.remove('over'); const fs = [...(e.dataTransfer?.files ?? [])].filter((f) => /\.exr$/i.test(f.name)); if (fs.length) void take(fs); };
  $<HTMLInputElement>('#depth-use', root).onchange = (e) => {
    st.depthOn = (e.target as HTMLInputElement).checked;
    send({ avr: 'depth-use', on: st.depthOn });
    touch();
  };
  // the preview checked them: write each as PNG and upload it now (Save records it)
  window.addEventListener('message', async (e) => {
    if (e.source !== frame.contentWindow || e.data?.avr !== 'depth-result') return;
    const v = Date.now().toString(36);
    for (const r of e.data.results as Array<{ id: string; ok: boolean; agree: number; note?: string; data?: Float32Array; w?: number; h?: number }>) {
      if (!panos.some((p) => p.id === r.id)) continue;
      if (!r.ok || !r.data) { notes.set(r.id, { text: r.note ?? 'Doesn’t match the model', state: 'warn' }); draw(); continue; }
      notes.set(r.id, { text: 'Uploading', state: 'busy' }); draw();
      try {
        const blob = await depthPng(r.data, r.w!, r.h!);
        const old = st.depth[r.id];
        if (project.storage_bytes + blob.size - (old?.bytes ?? 0) > project.storage_limit) throw new Error('This project is out of space');
        const key = `depth/${r.id}-${v}.png`;
        await uploadFile(project.id, key, blob);
        if (old && old.key !== st.depthSaved[r.id]?.key) void deleteFile(project.id, old.key);
        st.depth[r.id] = { key, bytes: blob.size };
        notes.set(r.id, { text: `Ready · ${Math.round(r.agree * 100)}% matches the model · save to keep`, state: 'ok' });
        touch();
      } catch (err) {
        notes.set(r.id, { text: err instanceof Error ? err.message : 'Upload failed', state: 'warn' });
      }
      draw();
    }
  });
}

async function saveSetup(project: Project, panos: PanoRow[], levels: LevelRow[], st: State): Promise<void> {
  const problems = altProblems(st.alt.alts, panos, (p) => st.rooms[p.id] || p.name);
  if (problems.length) throw new Error(problems[0]);
  // database: project settings and start, changed rooms and level names
  const settings: Record<string, unknown> = { ...(project.settings ?? {}), ...st.options, ...altSettings(st) };
  if (st.depthOn) delete settings.depth; else settings.depth = false;
  await update('projects', `id=eq.${project.id}`, {
    settings,
    ...(st.start ? { start_pano_id: st.start.pano, start_heading: st.start.heading, start_pitch: st.start.pitch } : {}),
  });
  project.settings = settings;
  if (st.password !== undefined) {
    const access = st.password ? await hashPassword(st.password) : { access_hash: null, access_salt: null, access_token: null };
    await update('projects', `id=eq.${project.id}`, access);
    project.access_hash = access.access_hash;
    st.password = undefined;
  }
  const changed = panos.filter((p) => (p.room ?? '') !== (st.rooms[p.id] ?? ''));
  for (let i = 0; i < changed.length; i += 6) {
    await Promise.all(changed.slice(i, i + 6).map((p) => update('panoramas', `id=eq.${p.id}`, { room: st.rooms[p.id] || null })));
  }
  changed.forEach((p) => (p.room = st.rooms[p.id] || null));
  // levels: new ones in, renders moved, changed ones updated, removed ones out
  const before = new Map(levels.map((l) => [l.id, l]));
  const fresh = st.levels.filter((l) => !before.has(l.id));
  if (fresh.length) await insert('levels', fresh.map((l) => ({ id: l.id, project_id: project.id, name: l.name, height: l.height, sort: st.levels.indexOf(l) })));
  const moved = panos.filter((p) => (p.level_id ?? null) !== (st.panoLevel[p.id] ?? null));
  for (let i = 0; i < moved.length; i += 6) {
    await Promise.all(moved.slice(i, i + 6).map((p) => update('panoramas', `id=eq.${p.id}`, { level_id: st.panoLevel[p.id] ?? null })));
  }
  moved.forEach((p) => (p.level_id = st.panoLevel[p.id] ?? null));
  await Promise.all(st.levels.filter((l) => before.has(l.id)).map((l) => {
    const o = before.get(l.id)!, sort = st.levels.indexOf(l);
    return o.name !== l.name || o.sort !== sort ? update('levels', `id=eq.${l.id}`, { name: l.name, sort }) : null;
  }));
  const gone = levels.filter((l) => !st.levels.some((x) => x.id === l.id));
  if (gone.length) await remove('levels', `id=in.(${gone.map((l) => l.id).join(',')})`);
  levels.splice(0, levels.length, ...st.levels.map((l, i) => ({ id: l.id, project_id: project.id, name: l.name, height: l.height, sort: i })));
  st.levels.forEach((l) => delete l.isNew);
  const hiddenLevels = st.levels.filter((l) => l.hidden).map((l) => l.id);
  if (JSON.stringify(hiddenLevels) !== JSON.stringify((project.settings as { hiddenLevels?: string[] } | undefined)?.hiddenLevels ?? [])) {
    const s2 = { ...(project.settings ?? {}), hiddenLevels };
    await update('projects', `id=eq.${project.id}`, { settings: s2 });
    project.settings = s2;
  }

  await replaceHotspots(project.id, st.hotspots);

  // alternate panoramas: rows share their main render's name, position and heading, in another set
  const altKey = (list: AltEdit[]) => JSON.stringify(list.map((a) => [a.id, a.pano, a.set.trim()]));
  if (altKey(st.alt.alts) !== altKey(st.altsSaved)) {
    await remove('panoramas', `project_id=eq.${project.id}&set_name=neq.default`);
    if (st.alt.alts.length) await insert('panoramas', st.alt.alts.map((a) => {
      const p = panos.find((x) => x.id === a.pano)!;
      return {
        id: a.id, project_id: project.id, name: p.name, room: null, set_name: a.set.trim(), level_id: st.panoLevel[p.id] ?? null,
        x: p.x, y: p.y, z: p.z, yaw: p.yaw, check_state: 'ok', base_key: a.base_key, mobile_key: a.mobile_key, full_key: a.full_key, bytes: a.bytes, sort: p.sort,
      };
    }));
    const kept = new Set(st.alt.alts.map((a) => a.id));
    const gone = st.altsSaved.filter((a) => !kept.has(a.id));
    await Promise.all(gone.flatMap((a) => [`thumbs/${a.id}.jpg`, a.base_key, a.mobile_key, a.full_key].map((k) => deleteFile(project.id, k))));
    st.altsSaved = st.alt.alts.map((a) => ({ ...a }));
  }

  // depth maps: rows carry the file and its size (part of the render's storage)
  const depthChanged = panos.filter((p) => (st.depth[p.id]?.key ?? null) !== (st.depthSaved[p.id]?.key ?? null));
  for (let i = 0; i < depthChanged.length; i += 6) {
    await Promise.all(depthChanged.slice(i, i + 6).map(async (p) => {
      const d = st.depth[p.id], was = st.depthSaved[p.id];
      const bytes = p.bytes - (was?.bytes ?? 0) + (d?.bytes ?? 0);
      await update('panoramas', `id=eq.${p.id}`, { depth_key: d?.key ?? null, depth_bytes: d?.bytes ?? 0, bytes });
      p.bytes = bytes; p.depth_key = d?.key ?? null; p.depth_bytes = d?.bytes ?? 0;
      project.storage_bytes += (d?.bytes ?? 0) - (was?.bytes ?? 0);
    }));
  }
  const depthGone = depthChanged.map((p) => st.depthSaved[p.id]?.key).filter((k): k is string => !!k);
  st.depthSaved = { ...st.depth };

  // the tour file the viewer reads
  const man = await getDraftManifest(project.id);
  man.hotspots = st.hotspots.map((h) => hotspotToManifest(h));
  man.panos = man.panos.map((p) => ({ ...p, room: st.rooms[p.id] || undefined }));
  man.levels = st.levels.map((l) => ({ name: l.name, height: +l.height.toFixed(3), ...(l.hidden ? { hidden: true } : {}) }));
  man.panos = man.panos.map((p) => { const i = st.levels.findIndex((l) => l.id === st.panoLevel[p.id]); return { ...p, level: i >= 0 ? i : null }; });
  man.settings = { ...(man.settings ?? {}), ...st.options, ...altSettings(st) };
  if (st.depthOn) delete man.settings.depth; else man.settings.depth = false;
  man.panos = man.panos.map((p) => {
    const { depth: _d, ...images } = p.images; void _d;
    const d = st.depth[p.id];
    return { ...p, images: d ? { ...images, depth: d.key } : images };
  });
  man.panos = man.panos.map((p) => {
    const alts = st.alt.alts.filter((a) => a.pano === p.id).map((a) => ({ id: a.id, set: a.set.trim(), images: { base: a.base_key, mobile: a.mobile_key, full: a.full_key } }));
    const { alts: _old, ...rest } = p; void _old;
    return alts.length ? { ...rest, alts } : rest;
  });
  if (st.start) man.start = { pano: st.start.pano, heading: st.start.heading, pitch: st.start.pitch };
  await uploadFile(project.id, 'manifest.json', new Blob([JSON.stringify(man)], { type: 'application/json' }));
  if (project.state === 'published') await syncPublished(project.id);
  // replaced and removed depth maps: nothing points at them any more
  await Promise.all(depthGone.map((k) => deleteFile(project.id, k)));
}

/**
 * Depth maps in Options: what the preview's depth meshes are made with and what the clean-up removed, so a
 * change to detail or clean-up can be seen working. Rebuild makes them again now.
 */
function wireDepthStats(root: HTMLElement, frame: HTMLIFrameElement): void {
  const out = $('#depth-stat', root);
  const btn = $<HTMLButtonElement>('#depth-rebuild', root);
  let rebuilt = 0;
  btn.onclick = () => { rebuilt = Date.now(); frame.contentWindow?.postMessage({ avr: 'depth-rebuild' }, location.origin); out.textContent = 'Rebuilding…'; };
  const n = (x: number) => x.toLocaleString('en-AU');
  const onMsg = (e: MessageEvent) => {
    if (e.source !== frame.contentWindow || e.data?.avr !== 'depth-stats') return;
    const d = e.data as { grid: number; clean: number; built: number; building: number; triangles: number; stray: number; loose: number; withDepth: number };
    if (!d.withDepth) { out.textContent = 'No depth maps loaded in the preview yet.'; return; }
    if (d.building || (!d.built && Date.now() - rebuilt < 4000)) { out.textContent = `Rebuilding… ${n(d.built)} ready`; return; }
    if (!d.built) { out.textContent = 'Walk the preview to build the depth meshes.'; return; }
    const removed = d.stray + d.loose;
    out.textContent = `${n(d.built)} mesh${d.built === 1 ? '' : 'es'} near you at ${n(d.grid)} × ${n(d.grid / 2)} · ${n(d.triangles)} triangles`
      + (d.clean > 0 ? ` · clean-up removed ${n(removed)} (${n(d.stray)} in open space, ${n(d.loose)} loose)` : ' · clean-up off');
  };
  addEventListener('message', onMsg);
  const t = setInterval(() => {
    if (!root.isConnected) { clearInterval(t); removeEventListener('message', onMsg); return; }
    if (out.offsetParent) frame.contentWindow?.postMessage({ avr: 'depth-stats' }, location.origin);
  }, 1000);
}
